import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Key, Copy, RotateCcw, Settings, Shield, Eye, EyeOff } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface PasswordOptions {
  length: number;
  includeUppercase: boolean;
  includeLowercase: boolean;
  includeNumbers: boolean;
  includeSymbols: boolean;
  excludeSimilar: boolean;
  excludeAmbiguous: boolean;
}

const PasswordGenerator: React.FC = () => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [options, setOptions] = useState<PasswordOptions>({
    length: 16,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: true,
    excludeSimilar: false,
    excludeAmbiguous: false
  });

  useSEO({
    title: 'Password Generator - Strong & Secure Passwords Online',
    description: 'Generate strong, secure passwords with customizable options. Include uppercase, lowercase, numbers, and symbols. Free online password generator.',
    keywords: 'password generator, strong password, secure password, random password, password creator, password maker',
    canonical: 'https://snap-tools.online/security/password-generator'
  });

  const characterSets = {
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    numbers: '0123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
    similar: 'il1Lo0O',
    ambiguous: '{}[]()/\\\'"`~,;.<>'
  };

  const generatePassword = useCallback(() => {
    setIsGenerating(true);
    
    try {
      let charset = '';
      
      if (options.includeUppercase) charset += characterSets.uppercase;
      if (options.includeLowercase) charset += characterSets.lowercase;
      if (options.includeNumbers) charset += characterSets.numbers;
      if (options.includeSymbols) charset += characterSets.symbols;
      
      if (!charset) {
        toast.error('Please select at least one character type');
        setIsGenerating(false);
        return;
      }
      
      // Remove similar characters if requested
      if (options.excludeSimilar) {
        charset = charset.split('').filter(char => !characterSets.similar.includes(char)).join('');
      }
      
      // Remove ambiguous characters if requested
      if (options.excludeAmbiguous) {
        charset = charset.split('').filter(char => !characterSets.ambiguous.includes(char)).join('');
      }
      
      if (charset.length === 0) {
        toast.error('No characters available with current settings');
        setIsGenerating(false);
        return;
      }
      
      // Generate password using crypto.getRandomValues for better randomness
      const array = new Uint8Array(options.length);
      crypto.getRandomValues(array);
      
      let newPassword = '';
      for (let i = 0; i < options.length; i++) {
        newPassword += charset[array[i] % charset.length];
      }
      
      // Ensure password meets requirements (has at least one of each selected type)
      const hasUppercase = options.includeUppercase ? /[A-Z]/.test(newPassword) : true;
      const hasLowercase = options.includeLowercase ? /[a-z]/.test(newPassword) : true;
      const hasNumbers = options.includeNumbers ? /[0-9]/.test(newPassword) : true;
      const hasSymbols = options.includeSymbols ? /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(newPassword) : true;
      
      if (hasUppercase && hasLowercase && hasNumbers && hasSymbols) {
        setPassword(newPassword);
        toast.success('Password generated successfully');
      } else {
        // Regenerate if password doesn't meet requirements
        generatePassword();
        return;
      }
      
    } catch (error) {
      console.error('Error generating password:', error);
      toast.error('Failed to generate password');
    }
    
    setIsGenerating(false);
  }, [options]);

  const copyToClipboard = async () => {
    if (!password) {
      toast.error('No password to copy');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(password);
      toast.success('Password copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return { score: 0, label: 'No Password', color: 'gray' };
    
    let score = 0;
    
    // Length scoring
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (pwd.length >= 16) score += 1;
    
    // Character type scoring
    if (/[a-z]/.test(pwd)) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 1;
    
    // Entropy scoring
    const uniqueChars = new Set(pwd.split('')).size;
    if (uniqueChars > pwd.length * 0.8) score += 1;
    
    if (score <= 3) return { score, label: 'Weak', color: 'red' };
    if (score <= 5) return { score, label: 'Medium', color: 'yellow' };
    if (score <= 7) return { score, label: 'Strong', color: 'green' };
    return { score, label: 'Very Strong', color: 'emerald' };
  };

  const strength = getPasswordStrength(password);

  const updateOption = (key: keyof PasswordOptions, value: boolean | number) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Key className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Password Generator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Generate strong, secure passwords with customizable options. Create unique passwords for all your accounts.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Generated Password */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Generated Password</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={copyToClipboard}
                  disabled={!password}
                  className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
              </div>
            </div>
            
            <div className="bg-white rounded border p-4 mb-4">
              <div className="font-mono text-lg break-all">
                {password ? (showPassword ? password : '•'.repeat(password.length)) : 'Click "Generate Password" to create a password'}
              </div>
            </div>
            
            {password && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Strength:</span>
                  <span className={`text-sm font-semibold text-${strength.color}-600`}>
                    {strength.label}
                  </span>
                </div>
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full bg-${strength.color}-500 transition-all duration-300`}
                    style={{ width: `${(strength.score / 8) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Password Options */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">Password Options</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password Length: {options.length}
                </label>
                <input
                  type="range"
                  min="4"
                  max="128"
                  value={options.length}
                  onChange={(e) => updateOption('length', parseInt(e.target.value))}
                  className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>4</span>
                  <span>128</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">Character Types</h4>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={options.includeUppercase}
                    onChange={(e) => updateOption('includeUppercase', e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Uppercase Letters (A-Z)</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={options.includeLowercase}
                    onChange={(e) => updateOption('includeLowercase', e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Lowercase Letters (a-z)</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={options.includeNumbers}
                    onChange={(e) => updateOption('includeNumbers', e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Numbers (0-9)</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={options.includeSymbols}
                    onChange={(e) => updateOption('includeSymbols', e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Symbols (!@#$%^&*)</span>
                </label>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="font-medium text-gray-700 mb-3">Advanced Options</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={options.excludeSimilar}
                    onChange={(e) => updateOption('excludeSimilar', e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Exclude Similar Characters (i, l, 1, L, o, 0, O)</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={options.excludeAmbiguous}
                    onChange={(e) => updateOption('excludeAmbiguous', e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Exclude Ambiguous Characters ({'{} [] () / \\ \' " ` ~ , ; . < >'})</span>
                </label>
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={generatePassword}
              disabled={isGenerating}
              className="flex-1 bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isGenerating ? 'Generating...' : 'Generate Password'}
            </button>
            
            <button
              onClick={() => setPassword('')}
              className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Clear
            </button>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Security Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h4 className="font-semibold text-blue-900 mb-3">Password Security Tips</h4>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• Use a unique password for each account</li>
              <li>• Passwords should be at least 12 characters long</li>
              <li>• Include a mix of uppercase, lowercase, numbers, and symbols</li>
              <li>• Never share your passwords with others</li>
              <li>• Use a password manager to store passwords securely</li>
              <li>• Enable two-factor authentication when available</li>
            </ul>
          </div>

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Password Best Practices</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>Length Matters:</strong> Longer passwords are exponentially harder to crack</li>
                <li>• <strong>Complexity:</strong> Mix different character types for maximum security</li>
                <li>• <strong>Uniqueness:</strong> Never reuse passwords across different services</li>
                <li>• <strong>Regular Updates:</strong> Change passwords periodically, especially for critical accounts</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3">What to Avoid</h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• Personal information (names, birthdays, addresses)</li>
                <li>• Common words or phrases</li>
                <li>• Sequential numbers or letters (123456, abcdef)</li>
                <li>• Simple patterns (qwerty, password123)</li>
                <li>• Storing passwords in plain text files or browsers</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PasswordGenerator;
